import type { DashboardView } from "../../redux/portfolio.types"
import {
  createEmptyFilterLabels,
  createEmptyFilters
} from "../../redux/globalFilters"
import type {
  CatalogWidgetSpec,
  WidgetCatalog,
  WidgetInstanceV4
} from "../../redux/widgets.types"
import { specToInstance } from "./widgetsResponseAdapter"

/**
 * The GC Default view.
 *
 * WHY THIS EXISTS AT ALL. Home used to render ONE composition whatever view
 * was selected — the saved view chose which widgets the grid held, never how
 * the page was arranged. So a "new default layout" had nowhere to live: every
 * account landed on `normalizeToV3`'s fallback, literally named "My View",
 * showing the legacy arrangement. GC Default is the first view whose identity
 * selects a COMPOSITION, not just a widget set.
 *
 * It is a real, persisted view — not a flag and not a branch on the user's
 * role — and it is the MAIN one: a one-time, versioned migration
 * (`HOME_DEFAULT_VERSION`) adds it to every account, seeds it with the full
 * default widget set and makes it active, keeping the account's previous
 * views intact and selectable. After that the stored config is the user's:
 * switching away, or removing a widget, is never undone on a later load.
 */
export const GC_DEFAULT_VIEW_ID = "gc_default"
export const GC_DEFAULT_VIEW_NAME = "GC Default"

/**
 * Bump when the default composition changes in a way existing accounts should
 * receive. A config whose `homeDefaultVersion` is below this is migrated once;
 * equal or above is left exactly as the user saved it.
 */
export const HOME_DEFAULT_VERSION = 1

export const isGcDefaultView = (viewId: string | null | undefined): boolean =>
  viewId === GC_DEFAULT_VIEW_ID

const makeGcDefaultView = (): DashboardView => ({
  id: GC_DEFAULT_VIEW_ID,
  key: GC_DEFAULT_VIEW_ID,
  name: GC_DEFAULT_VIEW_NAME,
  order: [],
  hidden: [],
  globalFilters: createEmptyFilters(),
  filterLabels: createEmptyFilterLabels()
})

/** Has this config already been migrated to the current Home default? */
export const hasHomeDefault = (raw: unknown): boolean => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false
  const version = (raw as { homeDefaultVersion?: unknown }).homeDefaultVersion
  return typeof version === "number" && version >= HOME_DEFAULT_VERSION
}

/**
 * Did this account ever save a dashboard layout of its own?
 *
 * A fresh account becomes GC Default outright (its "My View" is only a
 * placeholder for an absent config). A saved account keeps every view it has
 * and gains GC Default beside them — that is what "preserve the previous
 * layout as a recoverable saved view" means in practice.
 */
export const hasStoredLayout = (raw: unknown): boolean => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false
  const obj = raw as Record<string, unknown>
  if (Array.isArray(obj.views) && obj.views.length > 0) return true
  if (Array.isArray(obj.tiles) && obj.tiles.length > 0) return true
  if (Array.isArray(obj.order) && obj.order.length > 0) return true
  return false
}

/**
 * The widgets GC Default opens with, as CATALOG KEYS in section order.
 *
 * Keys rather than slugs, because the slug is derived from the catalog spec
 * (`specToInstance`) and the catalog is already filtered to what this user
 * may read — a key the catalog does not offer is simply not seeded, which is
 * how permissions are respected without a second rule here. Cash Flow and
 * Project Status are the always-on fixed instances and are added separately.
 */
export const DEFAULT_WIDGET_KEYS: readonly string[] = [
  // At a glance — the band reads these KPIs; Next Best Actions feeds the cards.
  "active_projects",
  "delayed_projects",
  "pending_approvals",
  "nba",
  // Financial — the band's money figures, the snapshot beside Cash Flow.
  "revenue_in_window",
  "outstanding_receivables",
  "portfolio_financial_snapshot",
  // Schedule
  "upcoming_milestones",
  // Pipeline
  "sales_pipeline",
  "win_rate",
  "activity_feed"
]

/**
 * The default widget instances this user can actually have.
 *
 * Resolved against the loaded catalog so the result is permission-filtered
 * and carries the exact slugs the rest of the dashboard keys on. Order is the
 * section order above, which is also the reading order the page renders.
 */
export const defaultInstancesFrom = (
  catalog: WidgetCatalog | null,
  fixed: WidgetInstanceV4[]
): WidgetInstanceV4[] => {
  const byKey = new Map<string, CatalogWidgetSpec>(
    (catalog?.widgets ?? []).map((spec) => [spec.key, spec])
  )
  const seeded: WidgetInstanceV4[] = []
  const seen = new Set<string>()
  for (const key of DEFAULT_WIDGET_KEYS) {
    const spec = byKey.get(key)
    if (!spec) continue
    const instance = specToInstance(spec)
    if (seen.has(instance.slug)) continue
    seen.add(instance.slug)
    seeded.push(instance)
  }
  for (const instance of fixed) {
    if (!seen.has(instance.slug)) {
      seen.add(instance.slug)
      seeded.push(instance)
    }
  }
  return seeded
}

/**
 * Puts GC Default in the view list and decides which view opens.
 *
 * `migrated` is whether the stored config already carries the current
 * `homeDefaultVersion`. Until it does, GC Default is added (never replacing a
 * personal view) and made active — the one-time migration. Once it does, the
 * stored active view is the user's own later choice and is respected.
 */
export const withGcDefaultView = (
  views: DashboardView[],
  activeViewId: string | null,
  storedLayout: boolean,
  migrated: boolean
): { views: DashboardView[]; activeViewId: string | null } => {
  if (!storedLayout) {
    return { views: [makeGcDefaultView()], activeViewId: GC_DEFAULT_VIEW_ID }
  }
  const present = views.some((view) => view.id === GC_DEFAULT_VIEW_ID)
  const next = present ? views : [...views, makeGcDefaultView()]
  if (!migrated) {
    return { views: next, activeViewId: GC_DEFAULT_VIEW_ID }
  }
  return { views: next, activeViewId: activeViewId ?? next[0]?.id ?? null }
}
